'use client';

import type { ReactNode } from 'react';
import { useState } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { MarkdownPreview } from '../../../markdown-preview';
import {
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceLine,
} from '../trace';
import { CHAT_STEP_BODY_FONT_SIZE_PX } from '../chat-nesting-layout';
import {
  collapseConsecutiveDuplicateActivityRows,
  type ToolCallActivityRow,
} from './tool-call-activity-row.utils';

export type { ToolCallActivityRow } from './tool-call-activity-row.utils';
export {
  collapseConsecutiveDuplicateActivityRows,
  toCodeAnalyzerActivityRow,
  toDetailFirstActivityRow,
  toStageWithDetailActivityRow,
} from './tool-call-activity-row.utils';

function ExpandableActivityRow({
  row,
  shimmer,
}: {
  row: ToolCallActivityRow;
  shimmer: boolean;
}): ReactNode {
  const [isExpanded, setIsExpanded] = useState(false);

  const status = row.isError ? 'error' : shimmer ? 'running' : undefined;

  return (
    <TraceCollapsible
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
    >
      <TraceCollapsibleTrigger>
        <TraceLine
          title={row.label}
          description={isExpanded ? undefined : row.summary}
          canExpand
          isExpanded={isExpanded}
          status={status}
        />
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent open={isExpanded}>
        <VStack gap="0" pr={2} pt={1} alignItems="stretch">
          <Box color="fg.muted">
            <MarkdownPreview
              source={row.content ?? ''}
              compact
              fontSize={CHAT_STEP_BODY_FONT_SIZE_PX}
            />
          </Box>
        </VStack>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

export function ToolCallActivityRows({
  rows,
  shimmerLastRow = false,
}: {
  rows: ToolCallActivityRow[];
  shimmerLastRow?: boolean;
}): ReactNode {
  const visibleRows = collapseConsecutiveDuplicateActivityRows(rows);

  if (visibleRows.length === 0) {
    return null;
  }

  return (
    <VStack gap={0} alignItems="stretch" w="full">
      {visibleRows.map((row, index) => {
        const shimmer = shimmerLastRow && index === visibleRows.length - 1;

        if (row.content) {
          return (
            <ExpandableActivityRow key={row.id} row={row} shimmer={shimmer} />
          );
        }

        return (
          <TraceLine
            key={row.id}
            title={row.label}
            description={row.summary}
            status={row.isError ? 'error' : shimmer ? 'running' : undefined}
          />
        );
      })}
    </VStack>
  );
}
